import { useState, useEffect, useCallback } from "react";
import { API_ENDPOINT } from "../api";
import { authenticatedFetch } from "../auth";
import { useNavigate } from "react-router-dom";

const HealthCheck = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [usersOffset, setUsersOffset] = useState(0);
    const [assessmentsOffset, setAssessmentsOffset] = useState(0);
    const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
    const [loadingMoreAssessments, setLoadingMoreAssessments] = useState(false);
    const [selectedSentence, setSelectedSentence] = useState(null);
    // Teacher sign-up code management
    const [newCode, setNewCode] = useState("");
    const [newCodeLabel, setNewCodeLabel] = useState("");
    const [codeBusy, setCodeBusy] = useState(false);
    const [codeError, setCodeError] = useState("");
    // Student/teacher role switching in the users table
    const [roleBusyId, setRoleBusyId] = useState(null);
    const [roleError, setRoleError] = useState("");
    const navigate = useNavigate();

    const fetchHealthCheckData = useCallback(
        async (usersOff = 0, assessmentsOff = 0, append = false) => {
            const usersLimit = 5;
            const assessmentsLimit = 5;

            const healthCheckResponse = await authenticatedFetch(
                `${API_ENDPOINT}/dashboard/healthcheck/?users_offset=${usersOff}&users_limit=${usersLimit}&assessments_offset=${assessmentsOff}&assessments_limit=${assessmentsLimit}`,
            );

            if (!healthCheckResponse.ok) {
                throw new Error("Failed to fetch health check data");
            }

            const healthCheckData = await healthCheckResponse.json();

            if (append) {
                // Append new data to existing data
                setData((prevData) => ({
                    ...healthCheckData,
                    users: {
                        ...healthCheckData.users,
                        recent: [
                            ...(prevData?.users?.recent || []),
                            ...healthCheckData.users.recent,
                        ],
                    },
                    pronunciation_assessments: {
                        ...healthCheckData.pronunciation_assessments,
                        recent: [
                            ...(prevData?.pronunciation_assessments?.recent || []),
                            ...healthCheckData.pronunciation_assessments.recent,
                        ],
                    },
                }));
            } else {
                setData(healthCheckData);
            }
        },
        [],
    );

    useEffect(() => {
        const checkSuperuserAndFetchData = async () => {
            try {
                // First check if user is superuser
                const userInfoResponse = await authenticatedFetch(
                    `${API_ENDPOINT}/auth/user-info/`,
                );

                if (!userInfoResponse.ok) {
                    if (userInfoResponse.status === 401) {
                        navigate("/login", { state: { from: { pathname: "/healthcheck" } } });
                        return;
                    }
                    throw new Error("Failed to fetch user info");
                }

                const userInfoData = await userInfoResponse.json();
                setUserInfo(userInfoData);

                if (!userInfoData.is_superuser) {
                    setError("Access denied. You must be a superuser to view this page.");
                    setLoading(false);
                    return;
                }

                // Fetch healthcheck data
                await fetchHealthCheckData(0, 0, false);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        checkSuperuserAndFetchData();
    }, [navigate, fetchHealthCheckData]);

    const loadMoreUsers = async () => {
        setLoadingMoreUsers(true);
        try {
            const newOffset = usersOffset + 5;
            await fetchHealthCheckData(newOffset, assessmentsOffset, true);
            setUsersOffset(newOffset);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingMoreUsers(false);
        }
    };

    const loadMoreAssessments = async () => {
        setLoadingMoreAssessments(true);
        try {
            const newOffset = assessmentsOffset + 5;
            await fetchHealthCheckData(usersOffset, newOffset, true);
            setAssessmentsOffset(newOffset);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingMoreAssessments(false);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const openSentenceModal = (reference, recognized) => {
        setSelectedSentence({ reference, recognized });
    };

    const closeSentenceModal = () => {
        setSelectedSentence(null);
    };

    // Apply the codes list returned by any teacher-code mutation
    const applyCodes = (codes) => {
        setData((prev) => ({ ...prev, teacher_signup_codes: codes }));
    };

    const teacherCodeRequest = async (method, body) => {
        setCodeBusy(true);
        setCodeError("");
        try {
            const response = await authenticatedFetch(`${API_ENDPOINT}/dashboard/teacher-codes/`, {
                method,
                headers: { "Content-Type": "application/json" },
                ...(body && { body: JSON.stringify(body) }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(result.error || "Request failed");
            }
            if (result.codes) applyCodes(result.codes);
            return result;
        } catch (err) {
            setCodeError(err.message);
            throw err;
        } finally {
            setCodeBusy(false);
        }
    };

    const handleCreateCode = async (e) => {
        e.preventDefault();
        if (!newCode.trim()) return;
        try {
            await teacherCodeRequest("POST", {
                code: newCode.trim(),
                label: newCodeLabel.trim(),
            });
            setNewCode("");
            setNewCodeLabel("");
        } catch {
            /* error already surfaced via codeError */
        }
    };

    const handleToggleCode = (code) =>
        teacherCodeRequest("PATCH", { id: code.id, is_active: !code.is_active }).catch(() => {});

    const handleDeleteCode = (code) => {
        if (!window.confirm(`Delete sign-up code "${code.code}"? This cannot be undone.`)) return;
        teacherCodeRequest("DELETE", { id: code.id }).catch(() => {});
    };

    const handleRoleChange = async (user, role) => {
        setRoleBusyId(user.id);
        setRoleError("");
        try {
            const response = await authenticatedFetch(`${API_ENDPOINT}/dashboard/user-role/`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: user.id, role }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(result.error || "Failed to change role");
            }
            setData((prev) => ({
                ...prev,
                users: {
                    ...prev.users,
                    recent: prev.users.recent.map((u) =>
                        u.id === result.id ? { ...u, is_teacher: result.is_teacher } : u,
                    ),
                },
                teachers: { ...prev.teachers, ...result.teachers },
            }));
        } catch (err) {
            setRoleError(`${user.email}: ${err.message}`);
        } finally {
            setRoleBusyId(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-xl text-gray-600">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
                    <h2 className="text-xl font-bold mb-2">Error</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    const StatCard = ({ title, value, subtitle }) => (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
    );

    const TableSection = ({ title, children }) => (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            </div>
            <div className="overflow-x-auto">{children}</div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">System Health Check</h1>
                    <p className="text-gray-600">Database statistics and recent activity</p>
                    {userInfo && (
                        <p className="text-sm text-gray-500 mt-2">
                            Logged in as: {userInfo.email} (Administrator)
                        </p>
                    )}
                </div>

                {/* Users Section */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Users</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <StatCard title="Total Users" value={data?.users?.total || 0} />
                        <StatCard title="Active Users" value={data?.users?.active || 0} />
                        <StatCard title="Staff Users" value={data?.users?.staff || 0} />
                        <StatCard title="Superusers" value={data?.users?.superusers || 0} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <StatCard
                            title="New Users (7 days)"
                            value={data?.users?.new_last_7_days || 0}
                        />
                        <StatCard
                            title="New Users (30 days)"
                            value={data?.users?.new_last_30_days || 0}
                        />
                    </div>

                    {roleError && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {roleError}
                        </div>
                    )}

                    <TableSection title="Recent Users">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Date Joined
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Referrer
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data?.users?.recent?.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {user.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {user.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <select
                                                value={user.is_teacher ? "teacher" : "student"}
                                                onChange={(e) =>
                                                    handleRoleChange(user, e.target.value)
                                                }
                                                disabled={roleBusyId === user.id}
                                                aria-label={`Role for ${user.email}`}
                                                className="border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-900 disabled:opacity-50"
                                            >
                                                <option value="student">Student</option>
                                                <option value="teacher">Teacher</option>
                                            </select>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(user.date_joined).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`px-2 py-1 text-xs rounded-full ${
                                                    user.is_active
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-red-100 text-red-800"
                                                }`}
                                            >
                                                {user.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {user.referrer || "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableSection>

                    {data?.users?.has_more && (
                        <div className="mt-4 text-center">
                            <button
                                onClick={loadMoreUsers}
                                disabled={loadingMoreUsers}
                                className="px-6 py-2 bg-black text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                                {loadingMoreUsers ? "Loading..." : "Load More Users"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Teachers & Courses Section */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Teachers &amp; Courses</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <StatCard
                            title="Teacher Accounts"
                            value={data?.teachers?.total || 0}
                            subtitle={`${data?.teachers?.verified || 0} verified`}
                        />
                        <StatCard
                            title="New Teachers (7d / 30d)"
                            value={`${data?.teachers?.new_last_7_days || 0} / ${
                                data?.teachers?.new_last_30_days || 0
                            }`}
                        />
                        <StatCard title="Total Courses" value={data?.courses?.total || 0} />
                        <StatCard
                            title="New Courses (7d / 30d)"
                            value={`${data?.courses?.new_last_7_days || 0} / ${
                                data?.courses?.new_last_30_days || 0
                            }`}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <StatCard
                            title="Student Enrolments"
                            value={data?.courses?.total_memberships || 0}
                            subtitle="across all courses"
                        />
                        <StatCard
                            title="Course Sentences"
                            value={data?.courses?.total_sentences || 0}
                        />
                        <StatCard title="Course Folders" value={data?.courses?.total_folders || 0} />
                    </div>

                    <div className="mb-6">
                        <TableSection title="Courses by Language">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Language
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Count
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data?.courses?.by_language?.map((item, index) => (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.language}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.count}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!data?.courses?.by_language ||
                                        data.courses.by_language.length === 0) && (
                                        <tr>
                                            <td
                                                colSpan={2}
                                                className="px-6 py-4 text-sm text-gray-400"
                                            >
                                                No courses yet
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </TableSection>
                    </div>

                    <TableSection title="Recent Courses">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {[
                                        "ID",
                                        "Title",
                                        "Teacher",
                                        "Language",
                                        "Join Code",
                                        "Students",
                                        "Sentences",
                                        "Created",
                                    ].map((h) => (
                                        <th
                                            key={h}
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data?.courses?.recent?.map((course) => (
                                    <tr key={course.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {course.id}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                            {course.title}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                            {course.teacher__email || "-"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {course.language}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                                            {course.join_code}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {course.student_count}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {course.sentence_count}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(course.created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                                {(!data?.courses?.recent || data.courses.recent.length === 0) && (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-4 text-sm text-gray-400">
                                            No courses yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </TableSection>
                </div>

                {/* Teacher Sign-up Codes Section */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-1">Teacher Sign-up Codes</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Codes teachers must enter to register a teacher account during the beta. A
                        code can be shared with many teachers.
                    </p>

                    {/* Create form */}
                    <form
                        onSubmit={handleCreateCode}
                        className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4 flex flex-col sm:flex-row gap-3 sm:items-end"
                    >
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Code
                            </label>
                            <input
                                type="text"
                                value={newCode}
                                onChange={(e) => setNewCode(e.target.value)}
                                placeholder="e.g. SCHOOL-2026"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm tracking-wide"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Label (optional)
                            </label>
                            <input
                                type="text"
                                value={newCodeLabel}
                                onChange={(e) => setNewCodeLabel(e.target.value)}
                                placeholder="e.g. Munich pilot cohort"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={codeBusy || !newCode.trim()}
                            className="px-6 py-2 bg-primary hover:bg-purple-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                        >
                            {codeBusy ? "Saving..." : "Create Code"}
                        </button>
                    </form>
                    {codeError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm mb-4">
                            {codeError}
                        </div>
                    )}

                    <TableSection title="All Codes">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {["Code", "Label", "Status", "Created", "Actions"].map((h) => (
                                        <th
                                            key={h}
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data?.teacher_signup_codes?.map((code) => (
                                    <tr key={code.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-semibold text-gray-900">
                                            {code.code}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                                            {code.label || "-"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`px-2 py-1 text-xs rounded-full ${
                                                    code.is_active
                                                        ? "bg-green-100 text-green-800"
                                                        : "bg-gray-100 text-gray-600"
                                                }`}
                                            >
                                                {code.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(code.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                                            <button
                                                onClick={() => handleToggleCode(code)}
                                                disabled={codeBusy}
                                                className="text-primary hover:underline disabled:opacity-50"
                                            >
                                                {code.is_active ? "Deactivate" : "Activate"}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCode(code)}
                                                disabled={codeBusy}
                                                className="text-red-600 hover:underline disabled:opacity-50"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {(!data?.teacher_signup_codes ||
                                    data.teacher_signup_codes.length === 0) && (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-4 text-sm text-gray-400">
                                            No sign-up codes yet — create one above.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </TableSection>
                </div>

                {/* Waitlist Section */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Waitlist</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <StatCard title="Total Waitlist" value={data?.waitlist?.total || 0} />
                        <StatCard
                            title="New (7 days)"
                            value={data?.waitlist?.new_last_7_days || 0}
                        />
                        <StatCard
                            title="New (30 days)"
                            value={data?.waitlist?.new_last_30_days || 0}
                        />
                    </div>

                    <TableSection title="Recent Waitlist Entries">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Joined Waitlist
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data?.waitlist?.recent?.map((entry) => (
                                    <tr key={entry.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {entry.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {entry.email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(entry.created_at).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableSection>
                </div>

                {/* Pronunciation Assessments Section */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        Pronunciation Assessments
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        <StatCard
                            title="Total Assessments"
                            value={data?.pronunciation_assessments?.total || 0}
                        />
                        <StatCard
                            title="Last 7 Days"
                            value={data?.pronunciation_assessments?.last_7_days || 0}
                        />
                        <StatCard
                            title="Last 30 Days"
                            value={data?.pronunciation_assessments?.last_30_days || 0}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <StatCard
                            title="Avg Accuracy Score"
                            value={data?.pronunciation_assessments?.average_scores?.accuracy || 0}
                            subtitle="out of 100"
                        />
                        <StatCard
                            title="Avg Completeness Score"
                            value={
                                data?.pronunciation_assessments?.average_scores?.completeness || 0
                            }
                            subtitle="out of 100"
                        />
                        <StatCard
                            title="Avg Fluency Score"
                            value={data?.pronunciation_assessments?.average_scores?.fluency || 0}
                            subtitle="out of 100"
                        />
                    </div>

                    <div className="mb-6">
                        <TableSection title="Assessments by Language">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Language
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Count
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data?.pronunciation_assessments?.by_language?.map(
                                        (item, index) => (
                                            <tr key={index}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {item.language}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.count}
                                                </td>
                                            </tr>
                                        ),
                                    )}
                                </tbody>
                            </table>
                        </TableSection>
                    </div>

                    <TableSection title="Recent Assessments">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Language
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Reference Sentence
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        User Said
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Accuracy
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Completeness
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Fluency
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Origin
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Shares
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data?.pronunciation_assessments?.recent?.map((assessment) => (
                                    <tr key={assessment.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {assessment.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {assessment.user__email || "Anonymous"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {assessment.language}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(assessment.date).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            <div className="flex items-center gap-2 max-w-xs">
                                                <span
                                                    className="truncate flex-1"
                                                    title={assessment.sentence}
                                                >
                                                    {assessment.sentence || "-"}
                                                </span>
                                                {assessment.sentence && (
                                                    <button
                                                        onClick={() =>
                                                            copyToClipboard(assessment.sentence)
                                                        }
                                                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                                        title="Copy reference sentence"
                                                    >
                                                        <svg
                                                            className="w-4 h-4"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700">
                                            <div className="flex items-center gap-2 max-w-xs">
                                                <button
                                                    onClick={() =>
                                                        openSentenceModal(
                                                            assessment.sentence,
                                                            assessment.recognized_sentence,
                                                        )
                                                    }
                                                    className="truncate flex-1 text-left hover:text-blue-600 cursor-pointer"
                                                    title="Click to view full text"
                                                >
                                                    {assessment.recognized_sentence || "-"}
                                                </button>
                                                {assessment.recognized_sentence && (
                                                    <button
                                                        onClick={() =>
                                                            copyToClipboard(
                                                                assessment.recognized_sentence,
                                                            )
                                                        }
                                                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                                                        title="Copy recognized sentence"
                                                    >
                                                        <svg
                                                            className="w-4 h-4"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {assessment.accuracy_score?.toFixed(1) || "-"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {assessment.completeness_score?.toFixed(1) || "-"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {assessment.fluency_score?.toFixed(1) || "-"}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {assessment.origin_url ? (
                                                <a
                                                    href={assessment.origin_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 underline max-w-[150px] truncate inline-block align-bottom"
                                                    title={assessment.origin_url}
                                                >
                                                    {new URL(assessment.origin_url).hostname}
                                                </a>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {assessment.share_clicks}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableSection>

                    {data?.pronunciation_assessments?.has_more && (
                        <div className="mt-4 text-center">
                            <button
                                onClick={loadMoreAssessments}
                                disabled={loadingMoreAssessments}
                                className="px-6 py-2 bg-black text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                                {loadingMoreAssessments ? "Loading..." : "Load More Assessments"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Synthesized Audio Files Section */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        Synthesized Audio Files
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <StatCard
                            title="Total Files"
                            value={data?.synthesized_audio_files?.total || 0}
                        />
                        <StatCard
                            title="Accessed (7 days)"
                            value={data?.synthesized_audio_files?.accessed_last_7_days || 0}
                        />
                        <StatCard
                            title="Accessed (30 days)"
                            value={data?.synthesized_audio_files?.accessed_last_30_days || 0}
                        />
                    </div>

                    <div className="mb-6">
                        <TableSection title="Files by Language">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Language
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                            Count
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {data?.synthesized_audio_files?.by_language?.map(
                                        (item, index) => (
                                            <tr key={index}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {item.language}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {item.count}
                                                </td>
                                            </tr>
                                        ),
                                    )}
                                </tbody>
                            </table>
                        </TableSection>
                    </div>

                    <TableSection title="Recent Audio Files">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Language
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Sentence
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Filename
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Last Access
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data?.synthesized_audio_files?.recent?.map((file) => (
                                    <tr key={file.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {file.id}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {file.language}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                                            {file.sentence}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs">
                                            {file.filename}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(file.last_access).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableSection>
                </div>

                {/* Sentence Modal */}
                {selectedSentence && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        onClick={closeSentenceModal}
                    >
                        <div
                            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Sentence Details
                                </h3>
                                <button
                                    onClick={closeSentenceModal}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg
                                        className="w-6 h-6"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <div className="px-6 py-4 space-y-4">
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            Reference Sentence
                                        </label>
                                        <button
                                            onClick={() =>
                                                copyToClipboard(selectedSentence.reference)
                                            }
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                />
                                            </svg>
                                            Copy
                                        </button>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded border border-gray-200">
                                        <p className="text-gray-900 whitespace-pre-wrap break-words">
                                            {selectedSentence.reference || "-"}
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-sm font-medium text-gray-700">
                                            User Said (Recognized)
                                        </label>
                                        <button
                                            onClick={() =>
                                                copyToClipboard(selectedSentence.recognized)
                                            }
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                />
                                            </svg>
                                            Copy
                                        </button>
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded border border-blue-200">
                                        <p className="text-gray-900 whitespace-pre-wrap break-words">
                                            {selectedSentence.recognized || "-"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                                <button
                                    onClick={closeSentenceModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HealthCheck;
